import { useState, useMemo, useEffect, useCallback } from 'react';
import { Moon } from 'lucide-react';
import PlanningWizard from '../components/planning/PlanningWizard';
import TodayWrapUp from '../components/planning/TodayWrapUp';
import InboxProcessor from '../components/planning/InboxProcessor';
import ScheduleReview from '../components/planning/ScheduleReview';
import OneOnOnePrep from '../components/planning/OneOnOnePrep';
import PlanningComplete from '../components/planning/PlanningComplete';
import ProjectModal from '../components/ProjectModal';
import GoalModal from '../components/GoalModal';
import LinkModal from '../components/LinkModal';
import OneOnOnePickerModal from '../components/OneOnOnePickerModal';
import { useTasks, TASK_STATUSES } from '../hooks/useTasks';
import { useProjects } from '../hooks/useProjects';
import { useGoals } from '../hooks/useGoals';
import { useLinks } from '../hooks/useLinks';
import { useOneOnOnes, getWeekStartForMeetingDay } from '../hooks/useOneOnOnes';
import { useOneOnOneRelationships } from '../hooks/useOneOnOneRelationships';
import { useTopics } from '../hooks/useTopics';
import { useActionItems } from '../hooks/useActionItems';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { useAuth } from '../hooks/useAuth';
import { getTomorrowString, getLocalDateString, getTodayString } from '../utils/dateUtils';
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import FeatureOnboarding from '../components/common/FeatureOnboarding';

function getInternalUserName(displayName) {
  if (!displayName) return null;
  const firstName = displayName.split(' ')[0];
  if (firstName === 'Jason') return 'Jason';
  if (firstName === 'Matt' || firstName === 'Matthew') return 'Matt';
  return firstName;
}

export default function EODPlanningPage() {
  const { user } = useAuth();
  const currentUserName = getInternalUserName(user?.displayName);

  const [currentStep, setCurrentStep] = useState(1);
  const [isComplete, setIsComplete] = useState(false);
  const [processedInboxCount, setProcessedInboxCount] = useState(0);
  const [initialInboxCount, setInitialInboxCount] = useState(0);

  // Quick action modal states
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showOneOnOnePickerModal, setShowOneOnOnePickerModal] = useState(false);
  const [currentInboxItem, setCurrentInboxItem] = useState(null);
  const [prefillData, setPrefillData] = useState(null);

  // Task management
  const {
    tasks,
    getInboxTasks,
    getUnassignedTasks,
    addToToday,
    moveToWaiting,
    moveToSomeday,
    scheduleTask,
    deleteTask,
    assignToBlock,
    completeTask,
    updateTask
  } = useTasks(currentUserName);

  // Quick action hooks
  const { addProject } = useProjects(currentUserName);
  const { addGoal } = useGoals();
  const { addLink } = useLinks();

  // Google Calendar
  const {
    isSignedIn: gcalSignedIn,
    getTimeBlocksForDate,
    getAllEventsForDate,
    createTimeBlock,
    TIME_BLOCK_NAMES
  } = useGoogleCalendar();

  // Tomorrow's date
  const tomorrowDate = useMemo(() => getTomorrowString(), []);

  // Tomorrow's events (all) and time blocks
  const [tomorrowEvents, setTomorrowEvents] = useState([]);
  const [tomorrowBlocks, setTomorrowBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(false);

  // Fetch tomorrow's events and blocks
  useEffect(() => {
    const fetchEvents = async () => {
      if (!gcalSignedIn || !getAllEventsForDate) return;

      setBlocksLoading(true);
      try {
        // Get ALL events for tomorrow
        const allEvents = await getAllEventsForDate(tomorrowDate);
        setTomorrowEvents(allEvents);

        // Filter to only time blocks
        const timeBlocks = allEvents.filter(event =>
          TIME_BLOCK_NAMES.some(name =>
            event.summary?.toLowerCase().includes(name.toLowerCase())
          )
        );
        setTomorrowBlocks(timeBlocks);
      } catch (error) {
        console.error('Error fetching tomorrow events:', error);
      } finally {
        setBlocksLoading(false);
      }
    };

    fetchEvents();
  }, [gcalSignedIn, getAllEventsForDate, tomorrowDate, TIME_BLOCK_NAMES]);

  // Get current inbox items
  const inboxItems = useMemo(() => getInboxTasks(), [getInboxTasks, tasks]);

  // Set initial inbox count on first render
  useEffect(() => {
    if (initialInboxCount === 0 && inboxItems.length > 0) {
      setInitialInboxCount(inboxItems.length);
    }
  }, [inboxItems.length, initialInboxCount]);

  // Get unassigned tasks (all)
  const unassignedTasks = useMemo(() => getUnassignedTasks(), [getUnassignedTasks, tasks]);

  // Get tasks scheduled for tomorrow but not assigned to a block
  const tomorrowUnassignedTasks = useMemo(() => {
    return tasks.filter(t =>
      t.scheduledDate === tomorrowDate &&
      !t.gcalTimeBlockId &&
      !t.completedAt
    );
  }, [tasks, tomorrowDate]);

  // Get today's tasks for wrap-up step (includes overdue)
  const todayAllTasks = useMemo(() => {
    const today = getTodayString();
    return tasks.filter(t =>
      !t.deleted &&
      !t.completedAt && (
        // Scheduled for today
        t.scheduledDate === today ||
        // Focused for today (but not from a past date)
        (t.focusedForToday === true && (!t.scheduledDate || t.scheduledDate >= today)) ||
        // Overdue: scheduled in the past
        (t.scheduledDate && t.scheduledDate < today)
      )
    );
  }, [tasks]);

  // Get tasks for a specific block
  const getTasksForBlock = useCallback((blockId) => {
    return tasks.filter(t => t.gcalTimeBlockId === blockId);
  }, [tasks]);

  // 1:1 Relationships for tomorrow
  const { activeRelationships } = useOneOnOneRelationships();

  // Get all 1:1s from tomorrow through end of week (Sunday), sorted by soonest
  const weekOneOnOnes = useMemo(() => {
    const tomorrow = new Date(tomorrowDate + 'T00:00:00');
    const tomorrowDayOfWeek = tomorrow.getDay();

    // Get remaining days through Sunday (0 = Sunday, 6 = Saturday)
    const remainingDays = [];
    for (let day = tomorrowDayOfWeek; day <= 6; day++) {
      remainingDays.push(day);
    }
    if (tomorrowDayOfWeek > 0) remainingDays.push(0); // Add Sunday at end

    // Filter and sort by soonest first
    return activeRelationships
      .filter(rel => remainingDays.includes(rel.meetingDay))
      .sort((a, b) => {
        const daysUntilA = a.meetingDay >= tomorrowDayOfWeek
          ? a.meetingDay - tomorrowDayOfWeek
          : 7 - tomorrowDayOfWeek + a.meetingDay;
        const daysUntilB = b.meetingDay >= tomorrowDayOfWeek
          ? b.meetingDay - tomorrowDayOfWeek
          : 7 - tomorrowDayOfWeek + b.meetingDay;
        return daysUntilA - daysUntilB;
      });
  }, [activeRelationships, tomorrowDate]);

  // State for 1:1 data
  const [oneOnOneData, setOneOnOneData] = useState({
    topicsMap: {},
    actionItemsMap: {},
    sessionsMap: {}
  });

  // Fetch topics and action items for tomorrow's 1:1s
  useEffect(() => {
    const fetchOneOnOneData = async () => {
      if (weekOneOnOnes.length === 0) {
        setOneOnOneData({ topicsMap: {}, actionItemsMap: {}, sessionsMap: {} });
        return;
      }

      const topicsMap = {};
      const actionItemsMap = {};
      const sessionsMap = {};

      for (const relationship of weekOneOnOnes) {
        const weekStart = getWeekStartForMeetingDay(relationship.meetingDay);

        // Query for session - include participants constraint for security rules
        const sessionsQuery = query(
          collection(db, 'oneOnOnes'),
          where('participants', 'array-contains', user.uid),
          where('relationshipId', '==', relationship.id),
          where('weekStart', '==', weekStart)
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);

        let sessionId;
        if (!sessionsSnapshot.empty) {
          sessionId = sessionsSnapshot.docs[0].id;
          sessionsMap[relationship.id] = {
            id: sessionId,
            ...sessionsSnapshot.docs[0].data()
          };

          // Query for topics - include participants constraint for security rules
          const topicsQuery = query(
            collection(db, 'topics'),
            where('participants', 'array-contains', user.uid),
            where('sessionId', '==', sessionId)
          );
          const topicsSnapshot = await getDocs(topicsQuery);
          topicsMap[relationship.id] = topicsSnapshot.docs.map(d => ({
            id: d.id,
            ...d.data()
          }));

          // Query for action items - include participants constraint for security rules
          const actionsQuery = query(
            collection(db, 'actionItems'),
            where('participants', 'array-contains', user.uid),
            where('sessionId', '==', sessionId)
          );
          const actionsSnapshot = await getDocs(actionsQuery);
          actionItemsMap[relationship.id] = actionsSnapshot.docs.map(d => ({
            id: d.id,
            ...d.data()
          }));
        } else {
          topicsMap[relationship.id] = [];
          actionItemsMap[relationship.id] = [];
        }
      }

      setOneOnOneData({ topicsMap, actionItemsMap, sessionsMap });
    };

    fetchOneOnOneData();
  }, [weekOneOnOnes]);

  // Trigger to refresh 1:1 data
  const [oneOnOneRefreshKey, setOneOnOneRefreshKey] = useState(0);
  const refreshOneOnOneData = () => setOneOnOneRefreshKey(k => k + 1);

  // Re-fetch when refresh key changes
  useEffect(() => {
    if (oneOnOneRefreshKey > 0) {
      // Trigger re-fetch by updating weekOneOnOnes dependency indirectly
      const fetchData = async () => {
        // Same logic as above
        if (weekOneOnOnes.length === 0) return;

        const topicsMap = {};
        const actionItemsMap = {};
        const sessionsMap = {};

        for (const relationship of weekOneOnOnes) {
          const weekStart = getWeekStartForMeetingDay(relationship.meetingDay);
          const sessionsQuery = query(
            collection(db, 'oneOnOnes'),
            where('participants', 'array-contains', user.uid),
            where('relationshipId', '==', relationship.id),
            where('weekStart', '==', weekStart)
          );
          const sessionsSnapshot = await getDocs(sessionsQuery);

          let sessionId;
          if (!sessionsSnapshot.empty) {
            sessionId = sessionsSnapshot.docs[0].id;
            sessionsMap[relationship.id] = { id: sessionId, ...sessionsSnapshot.docs[0].data() };

            const topicsQuery = query(collection(db, 'topics'), where('participants', 'array-contains', user.uid), where('sessionId', '==', sessionId));
            const topicsSnapshot = await getDocs(topicsQuery);
            topicsMap[relationship.id] = topicsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            const actionsQuery = query(collection(db, 'actionItems'), where('participants', 'array-contains', user.uid), where('sessionId', '==', sessionId));
            const actionsSnapshot = await getDocs(actionsQuery);
            actionItemsMap[relationship.id] = actionsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          } else {
            topicsMap[relationship.id] = [];
            actionItemsMap[relationship.id] = [];
          }
        }

        setOneOnOneData({ topicsMap, actionItemsMap, sessionsMap });
      };
      fetchData();
    }
  }, [oneOnOneRefreshKey, weekOneOnOnes]);

  // Handler for adding topic from 1:1 prep
  const handleAddOneOnOneTopic = async (sessionId, relationship, data) => {
    try {
      let actualSessionId = sessionId;
      const participants = relationship.participants || [];

      // If no session exists, create one
      if (!actualSessionId) {
        const weekStart = getWeekStartForMeetingDay(relationship.meetingDay);
        const newSession = {
          weekStart,
          relationshipId: relationship.id,
          participants: participants,
          status: 'active',
          createdAt: new Date()
        };
        const docRef = await addDoc(collection(db, 'oneOnOnes'), newSession);
        actualSessionId = docRef.id;
      }

      await addDoc(collection(db, 'topics'), {
        text: data.text,
        addedBy: data.addedBy || currentUserName || 'Unknown',
        sessionId: actualSessionId,
        participants: participants,
        discussed: false,
        createdAt: new Date()
      });

      refreshOneOnOneData();
    } catch (error) {
      console.error('Error adding topic:', error);
    }
  };

  // Handler for adding action item from 1:1 prep
  const handleAddOneOnOneActionItem = async (sessionId, relationship, data) => {
    try {
      let actualSessionId = sessionId;
      const participants = relationship.participants || [];

      // If no session exists, create one
      if (!actualSessionId) {
        const weekStart = getWeekStartForMeetingDay(relationship.meetingDay);
        const newSession = {
          weekStart,
          relationshipId: relationship.id,
          participants: participants,
          status: 'active',
          createdAt: new Date()
        };
        const docRef = await addDoc(collection(db, 'oneOnOnes'), newSession);
        actualSessionId = docRef.id;
      }

      await addDoc(collection(db, 'actionItems'), {
        text: data.text,
        assignedTo: data.assignedTo,
        dueDate: data.dueDate,
        sessionId: actualSessionId,
        participants: participants,
        completed: false,
        createdAt: new Date()
      });

      refreshOneOnOneData();
    } catch (error) {
      console.error('Error adding action item:', error);
    }
  };

  // Inbox processing handlers
  const handleAddToToday = async (taskId) => {
    await addToToday(taskId);
    setProcessedInboxCount(prev => prev + 1);
  };

  const handleMoveToWaiting = async (taskId) => {
    await moveToWaiting(taskId);
    setProcessedInboxCount(prev => prev + 1);
  };

  const handleMoveToSomeday = async (taskId) => {
    await moveToSomeday(taskId);
    setProcessedInboxCount(prev => prev + 1);
  };

  const handleSchedule = async (taskId, date) => {
    await scheduleTask(taskId, date);
    setProcessedInboxCount(prev => prev + 1);
  };

  const handleDelete = async (taskId) => {
    await deleteTask(taskId);
    setProcessedInboxCount(prev => prev + 1);
  };

  // Task assignment handler
  const handleAssignTask = async (taskId, blockId, blockName, scheduledDate) => {
    await assignToBlock(taskId, blockId, blockName, scheduledDate);
  };

  // Defer task to tomorrow (for wrap-up step)
  const handleDeferToTomorrow = async (taskId) => {
    // Clear time block assignment so task shows as unassigned in Step 3
    await updateTask(taskId, {
      scheduledDate: tomorrowDate,
      gcalTimeBlockId: null,
      gcalTimeBlockName: null,
      focusedForToday: false,
      focusDate: null,
      isSomeday: false
    });
  };

  // Create time block handler
  const handleCreateTimeBlock = async (startTime, endTime, blockName = 'Focus Block') => {
    if (!createTimeBlock) return null;
    try {
      // createTimeBlock(name, date, startTime, endTime)
      const newBlock = await createTimeBlock(blockName, tomorrowDate, startTime, endTime);
      // Refresh events
      if (getAllEventsForDate) {
        const allEvents = await getAllEventsForDate(tomorrowDate);
        setTomorrowEvents(allEvents);
        const timeBlocks = allEvents.filter(event =>
          TIME_BLOCK_NAMES.some(name =>
            event.summary?.toLowerCase().includes(name.toLowerCase())
          )
        );
        setTomorrowBlocks(timeBlocks);
      }
      return newBlock;
    } catch (error) {
      console.error('Error creating time block:', error);
      return null;
    }
  };

  // Quick action handlers
  const handleCreateProject = (item) => {
    setCurrentInboxItem(item);
    setPrefillData({
      name: item.title,
      description: item.notes || '',
      source: item.source,
      sourceChannel: item.sourceChannel,
      sourceLink: item.sourceLink
    });
    setShowProjectModal(true);
  };

  const handleSaveProject = async (projectData) => {
    await addProject(projectData);
    // Delete the inbox item after conversion
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
      setProcessedInboxCount(prev => prev + 1);
    }
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  const handleAddAsGoal = (item) => {
    setCurrentInboxItem(item);
    setPrefillData({
      title: item.title,
      description: item.notes || ''
    });
    setShowGoalModal(true);
  };

  const handleSaveGoal = async (goalData) => {
    await addGoal(goalData);
    // Delete the inbox item after conversion
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
      setProcessedInboxCount(prev => prev + 1);
    }
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  const handleAddAsHabit = (item) => {
    setCurrentInboxItem(item);
    setPrefillData({
      name: item.title,
      url: item.sourceLink || ''
    });
    setShowLinkModal(true);
  };

  const handleSaveHabit = async (linkData) => {
    await addLink(linkData);
    // Delete the inbox item after conversion
    if (currentInboxItem) {
      await deleteTask(currentInboxItem.id);
      setProcessedInboxCount(prev => prev + 1);
    }
    setCurrentInboxItem(null);
    setPrefillData(null);
  };

  const handleAddToOneOnOne = (item) => {
    setCurrentInboxItem(item);
    setShowOneOnOnePickerModal(true);
  };

  const handleOneOnOneSelect = async ({ relationship, type }) => {
    if (!currentInboxItem || !relationship) return;

    try {
      // Get or create the current session for this relationship
      const weekStart = getWeekStartForMeetingDay(relationship.meetingDay);
      const participants = relationship.participants || [];

      // Find the session for this week - include participants constraint for security rules
      const sessionsQuery = query(
        collection(db, 'oneOnOnes'),
        where('participants', 'array-contains', user.uid),
        where('relationshipId', '==', relationship.id),
        where('weekStart', '==', weekStart)
      );
      const sessionsSnapshot = await getDocs(sessionsQuery);

      let sessionId;
      if (sessionsSnapshot.empty) {
        // Create new session with participants
        const newSession = {
          weekStart,
          relationshipId: relationship.id,
          participants: participants,
          status: 'active',
          createdAt: new Date()
        };
        const docRef = await addDoc(collection(db, 'oneOnOnes'), newSession);
        sessionId = docRef.id;
      } else {
        sessionId = sessionsSnapshot.docs[0].id;
      }

      // Add to session based on type
      if (type === 'topic') {
        await addDoc(collection(db, 'topics'), {
          text: currentInboxItem.title,
          addedBy: currentUserName || 'Unknown',
          sessionId,
          participants: participants,
          discussed: false,
          createdAt: new Date()
        });
      } else if (type === 'actionItem') {
        // Default due date to end of current week (Friday)
        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysUntilFriday = dayOfWeek <= 5 ? 5 - dayOfWeek : 5 + (7 - dayOfWeek);
        const friday = new Date(today);
        friday.setDate(today.getDate() + daysUntilFriday);

        await addDoc(collection(db, 'actionItems'), {
          text: currentInboxItem.title,
          assignedTo: currentUserName || 'Jason',
          dueDate: getLocalDateString(friday),
          sessionId,
          participants: participants,
          completed: false,
          createdAt: new Date()
        });
      }

      // Delete the inbox item after conversion
      await deleteTask(currentInboxItem.id);
      setProcessedInboxCount(prev => prev + 1);
    } catch (error) {
      console.error('Error adding to 1:1:', error);
    }

    setCurrentInboxItem(null);
    setShowOneOnOnePickerModal(false);
  };

  // Handle finish
  const handleFinish = () => {
    setIsComplete(true);
  };

  // Handle done (close/reset)
  const handleDone = () => {
    setCurrentStep(1);
    setIsComplete(false);
    setProcessedInboxCount(0);
    setInitialInboxCount(0);
  };

  // Calculate stats for completion screen
  const completionStats = useMemo(() => {
    const tasksInBlocks = tasks.filter(t =>
      t.gcalTimeBlockId &&
      tomorrowBlocks.some(b => b.id === t.gcalTimeBlockId)
    );
    const blocksWithTasks = new Set(tasksInBlocks.map(t => t.gcalTimeBlockId)).size;

    return {
      inboxProcessed: processedInboxCount,
      tasksScheduled: tasksInBlocks.length,
      blocksUsed: blocksWithTasks,
      totalBlocks: tomorrowBlocks.length
    };
  }, [tasks, tomorrowBlocks, processedInboxCount]);

  // Show completion screen
  if (isComplete) {
    return (
      <div className="h-full flex flex-col bg-surface-base">
        <PlanningComplete
          stats={completionStats}
          onDone={handleDone}
        />
      </div>
    );
  }

  return (
    <FeatureOnboarding feature="dailyReview">
    <div className="h-full flex flex-col bg-surface-base">
      {/* Compact header with integrated stepper */}
      <PlanningWizard
        currentStep={currentStep}
        onStepChange={setCurrentStep}
        onFinish={handleFinish}
        canAdvance={true}
        inboxCount={inboxItems.length}
        processedCount={processedInboxCount}
      />

      {/* Step content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {currentStep === 1 && (
          <TodayWrapUp
            todayTasks={todayAllTasks}
            onComplete={completeTask}
            onDeferToTomorrow={handleDeferToTomorrow}
            onSchedule={scheduleTask}
            onDelete={deleteTask}
          />
        )}

        {currentStep === 2 && (
          <InboxProcessor
            inboxItems={inboxItems}
            onAddToToday={handleAddToToday}
            onMoveToWaiting={handleMoveToWaiting}
            onMoveToSomeday={handleMoveToSomeday}
            onSchedule={handleSchedule}
            onDelete={handleDelete}
            onCreateProject={handleCreateProject}
            onAddToOneOnOne={handleAddToOneOnOne}
            onAddAsGoal={handleAddAsGoal}
            onAddAsHabit={handleAddAsHabit}
            processedCount={processedInboxCount}
          />
        )}

        {currentStep === 3 && (
          <ScheduleReview
            tomorrowDate={tomorrowDate}
            allEvents={tomorrowEvents}
            timeBlocks={tomorrowBlocks}
            unassignedTasks={tomorrowUnassignedTasks}
            getTasksForBlock={getTasksForBlock}
            onAssignTask={handleAssignTask}
            onCreateTimeBlock={handleCreateTimeBlock}
            timeBlockNames={TIME_BLOCK_NAMES}
          />
        )}

        {currentStep === 4 && (
          <OneOnOnePrep
            tomorrowDate={tomorrowDate}
            weekOneOnOnes={weekOneOnOnes}
            topicsMap={oneOnOneData.topicsMap}
            actionItemsMap={oneOnOneData.actionItemsMap}
            sessionsMap={oneOnOneData.sessionsMap}
            currentUserName={currentUserName}
            onAddTopic={handleAddOneOnOneTopic}
            onAddActionItem={handleAddOneOnOneActionItem}
          />
        )}
      </div>

      {/* Quick action modals */}
      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => {
          setShowProjectModal(false);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveProject}
        editItem={prefillData}
      />

      <GoalModal
        isOpen={showGoalModal}
        onClose={() => {
          setShowGoalModal(false);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveGoal}
        editItem={prefillData}
      />

      <LinkModal
        isOpen={showLinkModal}
        onClose={() => {
          setShowLinkModal(false);
          setCurrentInboxItem(null);
          setPrefillData(null);
        }}
        onSave={handleSaveHabit}
        editItem={prefillData}
      />

      <OneOnOnePickerModal
        isOpen={showOneOnOnePickerModal}
        onClose={() => {
          setShowOneOnOnePickerModal(false);
          setCurrentInboxItem(null);
        }}
        onSelect={handleOneOnOneSelect}
        itemTitle={currentInboxItem?.title}
      />
    </div>
    </FeatureOnboarding>
  );
}
